import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading2,
  JustifyContent,
} from '@gousto-internal/citrus-react'

import type {
  NextBoxOfferData,
  SubscriptionOption,
  SubscriptionSettingsAnalytics,
  SubscriptionSettingsSection,
  SubscriptionStatusHeroData,
} from '@library/api-customer-engagement-experience'

import platesDesktop from '../assets/hero-plates-desktop.webp'
import platesMobile from '../assets/hero-plates-mobile.webp'
import platesTablet from '../assets/hero-plates-tablet.webp'
import { OfferBanner } from './OfferBanner'
import { OptionCard } from './OptionCard'

type SubscriptionSettingsOverviewProps = {
  sections: SubscriptionSettingsSection[]
  onAction: (intent: string, analytics?: SubscriptionSettingsAnalytics) => void
}

const findHero = (
  sections: SubscriptionSettingsSection[],
): SubscriptionStatusHeroData | undefined =>
  sections.find(
    (
      section,
    ): section is Extract<SubscriptionSettingsSection, { type: 'subscription_status_hero' }> =>
      section.type === 'subscription_status_hero',
  )?.data

const findOffer = (sections: SubscriptionSettingsSection[]): NextBoxOfferData | undefined =>
  sections.find(
    (section): section is Extract<SubscriptionSettingsSection, { type: 'next_box_offer' }> =>
      section.type === 'next_box_offer',
  )?.data

const findOptions = (sections: SubscriptionSettingsSection[]): SubscriptionOption[] =>
  sections.find(
    (section): section is Extract<SubscriptionSettingsSection, { type: 'subscription_options' }> =>
      section.type === 'subscription_options',
  )?.data.options ?? []

const MobileHeroArt = () => (
  <Box display={[Display.Block, Display.None, Display.None, Display.None]} paddingBottom={3}>
    <Box
      data-testid="hero-art-mobile"
      width="100%"
      style={{
        aspectRatio: '390 / 75',
        backgroundImage: `url(${platesMobile})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    />
  </Box>
)

const TabletHeroArt = () => (
  <Box
    data-testid="hero-art-tablet"
    borderRadius={1.5}
    flexGrow={1}
    minWidth="0"
    style={{
      aspectRatio: '722 / 254',
      maxWidth: '722px',
      backgroundImage: `url(${platesTablet})`,
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }}
  />
)

const DesktopHeroArt = () => (
  <Box
    data-testid="hero-art-desktop"
    borderRadius={1.5}
    width="760px"
    minWidth="0"
    style={{
      aspectRatio: '760 / 165',
      backgroundImage: `url(${platesDesktop})`,
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }}
  />
)

const HeroText = ({
  hero,
  centerHeight,
  maxWidth,
  minWidth,
  flexBasis,
}: {
  hero: SubscriptionStatusHeroData
  centerHeight?: string
  maxWidth?: string
  minWidth?: string
  flexBasis?: string
}) => (
  <Box
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    justifyContent={JustifyContent.Center}
    gap={[1, 4, 4, 4]}
    flexGrow={1}
    minWidth="0"
    style={{ minHeight: centerHeight, maxWidth, minWidth, flexBasis }}
  >
    <Heading2 size={[7, 10, 10, 10]} color={Color.ColdGrey_900}>
      {hero.headline}
    </Heading2>
    <Body2 size={4} color={Color.ColdGrey_700}>
      {hero.subtitle}
    </Body2>
  </Box>
)

const Hero = ({ hero }: { hero: SubscriptionStatusHeroData }) => (
  <>
    <Box display={[Display.Block, Display.None, Display.None, Display.None]}>
      <HeroText hero={hero} />
    </Box>
    <Box
      display={[Display.None, Display.Flex, Display.Flex, Display.None]}
      flexDirection={FlexDirection.Row}
      alignItems={AlignItems.Center}
      gap={[null, 3, 3, null]}
    >
      <HeroText hero={hero} centerHeight="165px" maxWidth="45%" />
      <TabletHeroArt />
    </Box>
    <Box
      display={[Display.None, Display.None, Display.None, Display.Flex]}
      flexDirection={FlexDirection.Row}
      alignItems={AlignItems.Center}
      gap={[null, null, null, 3]}
    >
      <HeroText hero={hero} centerHeight="165px" maxWidth="45%" minWidth="320px" flexBasis="0" />
      <DesktopHeroArt />
    </Box>
  </>
)

export const SubscriptionSettingsOverview = ({
  sections,
  onAction,
}: SubscriptionSettingsOverviewProps) => {
  const hero = findHero(sections)
  const offer = findOffer(sections)
  const options = findOptions(sections)

  return (
    <>
      {hero && <MobileHeroArt />}
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        paddingH={4}
        style={{ paddingTop: '8px' }}
      >
        {hero && (
          <Box paddingBottom={3}>
            <Hero hero={hero} />
          </Box>
        )}
        {offer && (
          <Box paddingBottom={[4, 6, 6, 6]}>
            <OfferBanner offer={offer} />
          </Box>
        )}
        <Box
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, FlexDirection.Row, null, null]}
          gap={[4, 6, 6, 6]}
        >
          {options.map((option) => (
            <OptionCard key={option.id} option={option} onAction={onAction} />
          ))}
        </Box>
      </Box>
    </>
  )
}
